import mixpanel from 'mixpanel-browser';
import { IAnalyticsProvider } from '@/integrations/analytics/types/IAnalyticsProvider';
import { IdentifyTraitsProperties } from '@/integrations/analytics/types/IdentifyTraitsProperties';
import { Env } from '@/Env';
import { MixpanelPeopleProperties } from '@/integrations/analytics/constants/MixpanelPeopleProperties';
import { MixpanelSuperProperties } from '@/integrations/analytics/constants/MixpanelSuperProperties';
import { MixpanelReservedProfileProperties } from '@/integrations/analytics/enums/MixpanelReservedProfileProperties';
export class MixpanelProvider implements IAnalyticsProvider {
  readonly name = 'Mixpanel';
  private initialized: boolean = false;

  init(): void {
    const token: string = Env.nodeEnv().VITE_APP_MIXPANEL_PROJECT_TOKEN || '';

    if (this.initialized || !token) {
      return;
    }

    mixpanel.init(token, {
      debug: Env.isDevelopment(),
      track_pageview: false,
      persistence: 'localStorage',
      ignore_dnt: true
    });

    this.initialized = true;
  }

  track(eventName: string, properties: Record<string, unknown>): void {
    if (!this.initialized) {
      return;
    }

    mixpanel.track(eventName, properties);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  page(name: string, properties: Record<string, unknown>, _options: Record<string, unknown>): void {
    if (!this.initialized) {
      return;
    }

    // Following the same pattern that segment uses for mixpanel page events
    const eventName: string = `Viewed ${name} Page`;

    mixpanel.track_pageview(properties, { event_name: eventName });
  }

  alias(userId: number): void {
    if (!this.initialized) {
      return;
    }

    mixpanel.alias(String(userId));
  }

  identify(userId: number | null, traits: IdentifyTraitsProperties): void {
    if (!this.initialized) {
      return;
    }

    if (userId) {
      mixpanel.identify(String(userId));
    }

    // Set user properties (people profile)
    if (traits && Object.keys(traits).length > 0) {
      mixpanel.people.set(this.getPeopleProperties(traits));

      // Register as super properties for all future events
      mixpanel.register(this.getSuperProperties(traits));
    }
  }

  reset(): void {
    if (!this.initialized) {
      return;
    }

    mixpanel.reset();
  }

  getPeopleProperties(traits: Record<string, unknown>): Record<string, unknown> {
    const allowedProperties: Set<string> = new Set(Object.values(MixpanelPeopleProperties));

    return Object.fromEntries(
      Object.entries(traits)
        .filter(([property]: [string, unknown]) =>
          allowedProperties.has(property as MixpanelPeopleProperties)
        )
        .map(([property, value]: [string, unknown]) => [
          MixpanelReservedProfileProperties.getReservedProfileProperty(property),
          value
        ])
    );
  }

  getSuperProperties(traits: Record<string, unknown>): Record<string, unknown> {
    const allowedProperties: Set<string> = new Set(Object.values(MixpanelSuperProperties));

    return Object.fromEntries(
      Object.entries(traits).filter(([property]: [string, unknown]) =>
        allowedProperties.has(property as MixpanelSuperProperties)
      )
    );
  }
}

export default new MixpanelProvider();
